import { useState, useEffect, useMemo, useCallback } from 'react';
import { Shield, Smartphone, Copy, Check, AlertTriangle, RefreshCw, Monitor, LogOut, Info } from 'lucide-react';
import { Card, CardHeader, CardTitle, Button, Spinner } from '@/components/ui';
import { useAuthStore, useSettingsStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import {
  getMfaStatus,
  regenerateBackupCodes,
  getActiveSessions,
  endOtherSessions,
  getStoredSessionId,
  setMfaSkip,
  getMfaSkipStatus,
  type MfaStatus,
  type SessionInfo,
  type UserRole,
} from '@perfin/shared';

// Sort order: standard users first (A-Z), then admins (A-Z), then superAdmin last
const ROLE_SORT_ORDER: Record<UserRole, number> = {
  standard: 0,
  admin: 1,
  superAdmin: 2,
};

const ROLE_LABELS: Record<UserRole, string> = {
  standard: 'User',
  admin: 'Admin',
  superAdmin: 'Super Admin',
};

export function SecuritySection() {
  const { user } = useAuthStore();
  const toast = useToast();
  const { members, currentUserRole } = useSettingsStore();
  const isSuperAdmin = currentUserRole === 'superAdmin';

  const [mfaStatus, setMfaStatus] = useState<MfaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [copiedBackupCodes, setCopiedBackupCodes] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // Active sessions state
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [endingOtherSessions, setEndingOtherSessions] = useState(false);

  // MFA Skip state (superAdmin only)
  const [mfaSkipMap, setMfaSkipMap] = useState<Record<string, boolean>>({});
  const [togglingUserId, setTogglingUserId] = useState<string | null>(null);

  // Sorted members: standard first, admins, superAdmin last
  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      const roleOrderA = ROLE_SORT_ORDER[a.role];
      const roleOrderB = ROLE_SORT_ORDER[b.role];
      if (roleOrderA !== roleOrderB) return roleOrderA - roleOrderB;
      const nameA = (a.displayName || a.email || '').toLowerCase();
      const nameB = (b.displayName || b.email || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [members]);

  // Load MFA status and active sessions
  useEffect(() => {
    async function loadSecurityData() {
      if (!user) return;
      try {
        const status = await getMfaStatus(user.uid);
        setMfaStatus(status);

        // Load active sessions
        const sessionId = getStoredSessionId();
        if (sessionId) {
          setSessionsLoading(true);
          try {
            const result = await getActiveSessions(sessionId);
            setSessions(result.sessions);
          } catch (sessionError) {
            console.warn('Failed to load sessions:', sessionError);
          } finally {
            setSessionsLoading(false);
          }
        }

        // Load MFA skip status for all members (superAdmin only)
        if (isSuperAdmin && members.length > 0) {
          try {
            const userIds = members.map((m) => m.userId);
            const skipStatus = await getMfaSkipStatus(userIds);
            setMfaSkipMap(skipStatus);
          } catch (skipError) {
            console.warn('Failed to load MFA skip status:', skipError);
          }
        }
      } catch (error) {
        console.error('Failed to load MFA status:', error);
        toast.error('Failed to load security settings');
      } finally {
        setLoading(false);
      }
    }
    loadSecurityData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isSuperAdmin, members.length]);

  // Regenerate backup codes
  const handleRegenerateBackupCodes = async () => {
    if (!user) return;

    setRegenerating(true);
    try {
      const newCodes = await regenerateBackupCodes(user.uid);
      setBackupCodes(newCodes);
      setShowBackupCodes(true);
      setMfaStatus((prev) => (prev ? { ...prev, backupCodesRemaining: 10 } : null));
      toast.success('New backup codes generated');
    } catch (error) {
      console.error('Failed to regenerate backup codes:', error);
      toast.error('Failed to generate new backup codes');
    } finally {
      setRegenerating(false);
    }
  };

  // Copy backup codes
  const handleCopyBackupCodes = async () => {
    if (!backupCodes) return;

    const codesText = backupCodes.join('\n');
    await navigator.clipboard.writeText(codesText);
    setCopiedBackupCodes(true);
    toast.success('Backup codes copied to clipboard');
    setTimeout(() => setCopiedBackupCodes(false), 3000);
  };

  // End all other sessions
  const handleEndOtherSessions = async () => {
    const sessionId = getStoredSessionId();
    if (!sessionId) return;

    setEndingOtherSessions(true);
    try {
      const result = await endOtherSessions(sessionId);
      if (result.endedCount > 0) {
        toast.success(`Logged out ${result.endedCount} other device${result.endedCount > 1 ? 's' : ''}`);
        // Refresh sessions list
        const sessionsResult = await getActiveSessions(sessionId);
        setSessions(sessionsResult.sessions);
      } else {
        toast.info('No other active sessions found');
      }
    } catch (error) {
      console.error('Failed to end other sessions:', error);
      toast.error('Failed to logout other devices');
    } finally {
      setEndingOtherSessions(false);
    }
  };

  // Toggle MFA skip for a user (superAdmin only)
  const handleToggleMfaSkip = useCallback(async (userId: string) => {
    const currentSkip = mfaSkipMap[userId] ?? false;
    const newSkip = !currentSkip;

    setTogglingUserId(userId);
    try {
      await setMfaSkip(userId, newSkip);
      setMfaSkipMap((prev) => ({ ...prev, [userId]: newSkip }));
      const member = members.find((m) => m.userId === userId);
      const name = member?.displayName || member?.email || 'User';
      toast.success(`MFA ${newSkip ? 'skip enabled' : 'required'} for ${name}`);
    } catch (error) {
      console.error('Failed to toggle MFA skip:', error);
      toast.error('Failed to update MFA setting');
    } finally {
      setTogglingUserId(null);
    }
  }, [mfaSkipMap, members, toast]);

  // Format session time
  const formatSessionTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className={`grid gap-6 ${isSuperAdmin ? 'grid-cols-1 lg:grid-cols-2 items-start' : 'max-w-2xl'}`}>
      {/* Column 1: Personal Security */}
      <div className="space-y-6">
      {/* MFA Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-gray-400" />
            <CardTitle>Two-Factor Authentication</CardTitle>
          </div>
        </CardHeader>

        <div className="space-y-4">
          {/* MFA Status */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Smartphone className="h-5 w-5 text-gray-400" />
              <div>
                <p className="font-medium text-gray-900">Authenticator App</p>
                <p className="text-sm text-gray-500">
                  Your account is protected with two-factor authentication
                </p>
              </div>
            </div>
            <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
              Required
            </span>
          </div>

          {/* Backup codes info */}
          {mfaStatus?.backupCodesRemaining !== undefined && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Backup codes remaining:</span>
                <span
                  className={
                    mfaStatus.backupCodesRemaining <= 2
                      ? 'font-medium text-red-600'
                      : 'font-medium text-gray-900'
                  }
                >
                  {mfaStatus.backupCodesRemaining} of 10
                </span>
              </div>

              {mfaStatus.backupCodesRemaining <= 2 && (
                <div className="flex items-center gap-2 p-3 text-sm text-amber-700 bg-amber-50 rounded-lg">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <p>You're running low on backup codes. Generate new ones.</p>
                </div>
              )}

              <Button
                variant="secondary"
                size="sm"
                onClick={handleRegenerateBackupCodes}
                loading={regenerating}
                leftIcon={<RefreshCw className="h-4 w-4" />}
              >
                Generate New Backup Codes
              </Button>
            </div>
          )}

          {/* Show new backup codes */}
          {showBackupCodes && backupCodes && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <h3 className="font-medium text-gray-900 mb-2">Your New Backup Codes</h3>
                <p className="text-sm text-gray-600">
                  Store these codes safely. Each can only be used once.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                {backupCodes.map((code, index) => (
                  <div key={index} className="p-2 bg-white rounded border text-center">
                    {code}
                  </div>
                ))}
              </div>

              <div className="flex gap-3 justify-center">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCopyBackupCodes}
                  leftIcon={copiedBackupCodes ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                >
                  {copiedBackupCodes ? 'Copied!' : 'Copy Codes'}
                </Button>
                <Button size="sm" onClick={() => setShowBackupCodes(false)}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Active Sessions */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Monitor className="h-5 w-5 text-gray-400" />
            <CardTitle>Active Sessions</CardTitle>
          </div>
        </CardHeader>

        <div className="space-y-4">
          {sessionsLoading ? (
            <div className="flex justify-center py-4">
              <Spinner size="sm" />
            </div>
          ) : sessions.length > 0 ? (
            <>
              {/* Session list */}
              <div className="space-y-3">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      session.isCurrent ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`p-2 rounded-lg ${
                          session.isCurrent ? 'bg-green-100' : 'bg-gray-100'
                        }`}
                      >
                        {session.deviceInfo.deviceType === 'mobile' ? (
                          <Smartphone className="h-4 w-4 text-gray-500" />
                        ) : (
                          <Monitor className="h-4 w-4 text-gray-500" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {session.deviceInfo.browser} on {session.deviceInfo.os}
                          {session.isCurrent && (
                            <span className="ml-2 text-xs text-green-600 font-normal">
                              (This device)
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          Last active: {formatSessionTime(session.lastActivityAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Logout other devices button */}
              {sessions.filter((s) => !s.isCurrent).length > 0 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleEndOtherSessions}
                  loading={endingOtherSessions}
                  leftIcon={<LogOut className="h-4 w-4" />}
                >
                  Logout All Other Devices
                </Button>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500 py-2">
              This is your only active session.
            </p>
          )}
        </div>
      </Card>

      {/* Security Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Security Tips</CardTitle>
        </CardHeader>
        <ul className="text-sm text-gray-600 space-y-2">
          <li className="flex items-start gap-2">
            <span className="text-green-500 mt-0.5">•</span>
            Use a strong, unique password for your account
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-500 mt-0.5">•</span>
            Store your backup codes in a secure location
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-500 mt-0.5">•</span>
            Never share your password or backup codes with anyone
          </li>
        </ul>
      </Card>
      </div>

      {/* Column 2: MFA Skip Configuration (superAdmin only) */}
      {isSuperAdmin && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-gray-400" />
                <CardTitle>Configure MFA Skip by User</CardTitle>
              </div>
            </CardHeader>

            <div className="space-y-4">
              {/* User table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-medium text-gray-600">User</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Role</th>
                      <th className="text-center py-2 px-3 font-medium text-gray-600">Skip MFA</th>
                      <th className="text-center py-2 px-3 font-medium text-gray-600">MFA Required?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMembers.map((member) => {
                      const isSkipped = mfaSkipMap[member.userId] ?? false;
                      const isToggling = togglingUserId === member.userId;

                      return (
                        <tr key={member.userId} className="border-b border-gray-100 last:border-0">
                          <td className="py-2.5 px-3">
                            <p className="font-medium text-gray-900">{member.displayName || member.email}</p>
                            {member.displayName && member.email && (
                              <p className="text-xs text-gray-500">{member.email}</p>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-gray-600">{ROLE_LABELS[member.role]}</td>
                          <td className="py-2.5 px-3 text-center">
                            <button
                              type="button"
                              disabled={isToggling}
                              onClick={() => handleToggleMfaSkip(member.userId)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/20 ${
                                isToggling ? 'opacity-50 cursor-wait' : 'cursor-pointer'
                              } ${isSkipped ? 'bg-amber-500' : 'bg-gray-300'}`}
                            >
                              <span
                                className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                                  isSkipped ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <span
                              className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                isSkipped
                                  ? 'text-red-700 bg-red-100'
                                  : 'text-green-700 bg-green-100'
                              }`}
                            >
                              {isSkipped ? 'No' : 'Yes'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Info note */}
              <div className="flex items-start gap-2 p-3 text-sm text-gray-600 bg-gray-50 rounded-lg">
                <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-gray-400" />
                <p>
                  MFA setup is still required for all users.
                  Skipped users bypass MFA verification on login only.
                  Their authenticator enrollment stays intact.
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
